import FileCopy from "@mui/icons-material/FileCopy";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Done from "@mui/icons-material/Done";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useStoreState } from "pullstate";
import { useEffect, useState } from "react";
import errorStore, { globalComponentName } from "stores/errorStore";
import { useTelemetry } from "@knowfully/ui.common";

type ErrorPageProps = {
  componentKey?: string;
  customErrorTitle?: string;
};

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  wrapper: {
    borderRadius: "5px",
    backgroundColor: theme.palette.primary.light,
    py: 2,
    px: 3,
    mb: 2,
  },
  accordionBox: {
    px: 0,
    py: 1,
  },
  accordion: {
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    textAlign: "left",
  },
  accordionSummary: {
    flexDirection: "row-reverse",
  },
  accordionDetails: {
    py: 0,
    backgroundColor: theme.palette.primary.light,
  },
  copyButton: {
    display: "flex",
    alignItems: "center",
    p: 2,
    pl: 0,
    ":hover": {
      backgroundColor: "unset",
    },
  },
  copyText: {
    textDecoration: "underline",
    mr: 0.5,
    color: "gray",
  },
  copyIconBox: {
    position: "relative",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  copyIcon: {
    color: "gray",
    transition: "opacity 0.3s ease, transform 0.3s ease",
    opacity: 1,
    transform: "scale(1)",
  },
  doneIcon: {
    color: "success.main",
    transition: "opacity 0.3s ease, transform 0.3s ease",
    opacity: 1,
    transform: "scale(1)",
  },
});

export const ErrorPage = ({
  componentKey,
  customErrorTitle,
}: ErrorPageProps) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [copied, setCopied] = useState(false);
  const telemetry = useTelemetry();

  const keyToUse = componentKey ?? globalComponentName;
  const errorData = useStoreState(errorStore)[keyToUse];

  useEffect(() => {
    if (errorData) {
      const error = new Error(errorData?.response?.data?.title || "Unknown kadmin error");
      telemetry.trackException(error, {
        componentKey: keyToUse,
        operationId: errorData?.response?.data?.operationId,
        status: errorData?.response?.data?.status,
        componentStack: errorData?.componentStack,
        ...errorData,
      });
    }
  }, [errorData, keyToUse, telemetry]);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(JSON.stringify(errorData, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      });
  };

  useEffect(() => {
    return () => {
      errorStore.update((s) => {
        s[keyToUse] = {};
      });
    };
  }, [keyToUse]);

  return (
    <Grid
      container
      display="flex"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      direction="column"
      sx={{ px:2 }}
    >
      <Grid item xs={12} md={6}>
        <Box sx={styles.wrapper}>
          <Typography variant="h4" gutterBottom>
            {customErrorTitle ?? "Sorry, this page isn't available"}
          </Typography>

          <Typography variant="body1">
            Try again later in a few minutes. If the problem persists, please
            contact the technology team
          </Typography>
        </Box>

        <Box sx={{ ...styles.wrapper, ...styles.accordionBox } as SxProps}>
          <Accordion sx={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="details-content"
              id="details-header"
              sx={styles.accordionSummary}
            >
              <Typography variant="h6">Details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>
              {errorData?.response?.data?.operationId && (
                <Typography variant="body1">
                  OperationId: {errorData.response.data.operationId}
                </Typography>
              )}

              <Button onClick={copyToClipboard} sx={styles.copyButton}>
                <Typography variant="body2" sx={styles.copyText}>
                  Copy error details to clipboard
                </Typography>
                <Box sx={styles.copyIconBox}>
                  {copied ? (
                    <Done sx={styles.doneIcon} />
                  ) : (
                    <FileCopy sx={styles.copyIcon} />
                  )}
                </Box>
              </Button>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Grid>
    </Grid>
  );
};
